/** Match a TM account request to an advice row: user code + site + email. */

export function normalizeTmEmail(value) {
  return String(value || "")
    .replace(/\u00a0/g, " ")
    .trim()
    .toLowerCase();
}

export function normalizeTmSite(value) {
  return String(value || "")
    .replace(/\u00a0/g, " ")
    .trim()
    .toLowerCase();
}

export function normalizeTmUserCode(value) {
  return String(value || "")
    .replace(/\u00a0/g, " ")
    .trim()
    .toUpperCase();
}

export function tmRequestRowKey(record) {
  if (!record) return "";
  const userCode = normalizeTmUserCode(record.user_code);
  const site = normalizeTmSite(record.site);
  const email = normalizeTmEmail(record.email);
  if (!userCode || !site || !email) return "";
  return `${userCode}|${site}|${email}`;
}

export function pendingTmRequestKeySet(requests = []) {
  const keys = new Set();
  for (const req of requests || []) {
    const key = tmRequestRowKey(req);
    if (key) keys.add(key);
  }
  return keys;
}

export function pendingTmRequestsByKey(requests = []) {
  const map = new Map();
  for (const req of requests || []) {
    const key = tmRequestRowKey(req);
    if (key) map.set(key, req);
  }
  return map;
}

export const TM_REQUEST_REASON_LABELS = {
  map_issue: "Map Issue",
  bad_queue_number: "Bad Queue Number",
  too_many_buys_transfers: "Too Many Buys/Transfers on Account",
  other: "Other",
};

export function tmRequestReasonLabel(reason) {
  return TM_REQUEST_REASON_LABELS[reason] || reason || "—";
}

export function isUseSameDetails(request) {
  const v = request?.use_same_details;
  return v === true || v === 1 || v === "1";
}

export function isCompletedTmRequest(request) {
  return String(request?.status || "").toLowerCase() === "completed";
}

/** Map completed request by the original email row key. */
export function completedTmByOriginalKey(requests = []) {
  const map = new Map();
  for (const req of requests || []) {
    if (!isCompletedTmRequest(req) || !req?.new_email) continue;
    const key = tmRequestRowKey(req);
    if (key) map.set(key, req);
  }
  return map;
}

/** Map completed request by the newly created email row key. */
export function completedTmByNewKey(requests = []) {
  const map = new Map();
  for (const req of requests || []) {
    if (!isCompletedTmRequest(req) || !req?.new_email) continue;
    const key = tmRequestRowKey({
      user_code: req.user_code,
      site: req.site,
      email: req.new_email,
    });
    if (key) map.set(key, req);
  }
  return map;
}

/**
 * Detect role terminology in search.
 * Returns 'restricted' | 'replacement' | null.
 * Tolerates the common typo "restrcited".
 */
export function tmAccountRoleSearchKind(query) {
  const q = String(query || "")
    .replace(/\u00a0/g, " ")
    .trim()
    .toLowerCase()
    .replace(/restrcited/g, "restricted")
    .replace(/\s+/g, " ");
  if (!q) return null;
  if (
    q.includes("order restricted") ||
    q.includes("restricted account") ||
    q === "restricted"
  ) {
    return "restricted";
  }
  if (
    q.includes("replacement account") ||
    q.includes("replaced account") ||
    q === "replacement" ||
    q === "replaced"
  ) {
    return "replacement";
  }
  return null;
}

export function adviceRowMatchesTmRoleSearch(
  row,
  roleKind,
  byOriginal = null,
  byNew = null
) {
  if (!roleKind) return false;
  const key = tmRequestRowKey(row);
  if (!key) return false;
  if (roleKind === "restricted") {
    return byOriginal instanceof Map && byOriginal.has(key);
  }
  if (roleKind === "replacement") {
    return byNew instanceof Map && byNew.has(key);
  }
  return false;
}

/** Ensure linked order-restricted / replacement rows stay together in results. */
export function expandAdviceRowsWithTmPairs(
  rows,
  allAdvice,
  byOriginal = null,
  byNew = null
) {
  const originalMap = byOriginal instanceof Map ? byOriginal : new Map();
  const newMap = byNew instanceof Map ? byNew : new Map();
  if (!rows?.length || (!originalMap.size && !newMap.size)) {
    return rows || [];
  }

  const selected = new Map();
  for (const row of rows) {
    if (!row) continue;
    const id = row.id != null ? `id:${row.id}` : `key:${tmRequestRowKey(row)}`;
    selected.set(id, row);
  }

  const partnerIds = new Set();
  const partnerKeys = new Set();
  for (const row of rows) {
    const key = tmRequestRowKey(row);
    if (!key) continue;
    const asOriginal = originalMap.get(key);
    if (asOriginal) {
      if (asOriginal.new_advice_password_id != null) {
        partnerIds.add(asOriginal.new_advice_password_id);
      }
      const partnerKey = tmRequestRowKey({
        user_code: asOriginal.user_code,
        site: asOriginal.site,
        email: asOriginal.new_email,
      });
      if (partnerKey) partnerKeys.add(partnerKey);
    }
    const asReplacement = newMap.get(key);
    if (asReplacement) {
      if (asReplacement.advice_password_id != null) {
        partnerIds.add(asReplacement.advice_password_id);
      }
      const partnerKey = tmRequestRowKey(asReplacement);
      if (partnerKey) partnerKeys.add(partnerKey);
    }
  }

  if (!partnerIds.size && !partnerKeys.size) {
    return rows;
  }

  for (const row of allAdvice || []) {
    if (!row) continue;
    const id = row.id != null ? `id:${row.id}` : `key:${tmRequestRowKey(row)}`;
    if (selected.has(id)) continue;
    if (row.id != null && partnerIds.has(row.id)) {
      selected.set(id, row);
      continue;
    }
    const key = tmRequestRowKey(row);
    if (key && partnerKeys.has(key)) {
      selected.set(id, row);
    }
  }

  return Array.from(selected.values());
}
